import React, { useEffect, useState } from "react";
import api from "./api";
import CallLogModal from "./CallLogModal";
import useAuth from "../hooks/useAuth";
import { canDo } from '../utils/canDo';

/* ===============================
   RISK CALCULATOR
================================ */
function calculatePatientRisk(appointments) {
  const today = new Date();
  let missed = 0;
  let lastVisit = null;
  let ongoingMissStreak = 0;

  const sorted = appointments
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  for (const a of sorted) {
    const isMissed =
      a.status.toLowerCase() === "skipped" ||
      (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
    if (isMissed) {
      missed++;
      ongoingMissStreak++;
    } else if (a.status.toLowerCase() === "completed") {
      if (!lastVisit) lastVisit = new Date(a.date);
      break;
    }
  }

  const stoppedDays = lastVisit ? Math.floor((today - lastVisit) / 86400000) : 60;
  let level = "LOW", color = "#4caf50";
  if (ongoingMissStreak >= 2) { level = "HIGH"; color = "#d32f2f"; }
  else if (missed > 0 || stoppedDays >= 14) { level = "MEDIUM"; color = "#ff9800"; }

  return {
    score: Math.round(ongoingMissStreak * 40 + missed * 10 + Math.min(stoppedDays, 60) * 0.5),
    level, color, missed, stoppedDays, ongoingMissStreak,
  };
}

function getRiskSituation(risk, appointments) {
  const { ongoingMissStreak, stoppedDays, missed } = risk;
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
  const hasConcerns = appointments.some(a =>
    (a.commentsByPt || "").trim().length > 0 && new Date(a.date) > thirtyDaysAgo
  );
  if (hasConcerns)            return { label: "🚩 Flagging Concerns",  color: "#7b1fa2", bg: "#f3e5f5" };
  if (stoppedDays > 45 && missed > 0) return { label: "🕳️ Silent Dropout", color: "#b71c1c", bg: "#ffebee" };
  if (ongoingMissStreak >= 3) return { label: "🚨 Dropping Out",       color: "#d32f2f", bg: "#ffebee" };
  if (ongoingMissStreak >= 2) return { label: "⚠️ Missing Sessions",   color: "#f57c00", bg: "#fff3e0" };
  if (stoppedDays > 21 && missed > 0) return { label: "📉 Gradually Slipping", color: "#e65100", bg: "#fff3e0" };
  if (ongoingMissStreak === 1) return { label: "😕 Just Missed Once",  color: "#f9a825", bg: "#fffde7" };
  if (missed === 0 && stoppedDays < 10) return { label: "✅ On Track", color: "#2e7d32", bg: "#e8f5e9" };
  if (missed === 0)           return { label: "🟢 Attending",          color: "#388e3c", bg: "#f1f8e9" };
  return                             { label: "📊 Monitoring",         color: "#1565c0", bg: "#e3f2fd" };
}

/* ===============================
   SESSION TIMELINE CHART
   Money-based: unpaid detection via cumulative visitCharges vs totalPaidSoFar
   Dividers: between session sets (new billing) and when paid budget exhausted
================================ */
// Therapist-type color mapping
function getTherapistType(appt) {
  const desig = (appt.leadTherapistDesignation || "").toLowerCase();
  const name  = (appt.leadTherapist || "").toLowerCase();
  if (desig.includes("doctor") || desig.includes("dr") || name.includes("deepak"))
    return "doctor";
  if (desig.includes("senior") || desig.includes("sr"))
    return "senior";
  return "therapist";
}
const THERAPIST_COLORS = {
  doctor:    "#b8860b", // golden
  senior:    "#1565c0", // blue
  therapist: "#1976d2", // blue
};

function isConsultationAppt(a) {
  const t = (a.title || a.name || "").toLowerCase();
  return t.includes("consultation");
}

function isConsultationSession(s) {
  return (s.serviceName || s.name || "").toLowerCase().includes("consultation");
}

/** Actual session count for a suggestedService/sessionSet item.
 *  Packages have _masterQty (sessions-per-package), so actual = qty × _masterQty.
 *  Non-packages: actual = qty. */
function actualSessionQty(s) {
  const qty = Number(s.qty) || 0;
  const masterQty = Number(s._masterQty) || 0;
  const isPkg = /package/i.test(s.name || s.serviceName || '') && masterQty > 1;
  return isPkg ? qty * masterQty : qty;
}

function SessionTimeline({ appointments, completedSessions: rawCompleted, today, totalPaidSoFar, overallVisitCharges, sessionSets, bills: billsRaw, canSeeFinancials, recommendedSessions: recSess, consultation: consultObj }) {
  const paid = Number(totalPaidSoFar) || 0;
  const billsList = billsRaw || [];

  // Filter out consultation appointments — they act as dividers, not sessions
  const therapyAppts = appointments.filter(a => !isConsultationAppt(a));
  // Consultation appointments for stacking with billing boxes
  const consultAppts = appointments.filter(a => isConsultationAppt(a));
  const completedSessions = therapyAppts.filter(a => a.status.toLowerCase() === "completed").length;

  // Sort sessionSets by date for divider logic — exclude consultation-only entries
  const sortedSets = (sessionSets || [])
    .filter(s => !isConsultationSession(s))
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Total qty bought across all sets (consultation-only already excluded)
  const totalQtyBought = sortedSets.reduce((sum, s) => sum + (Number(s.qty) || 0), 0);

  // Build a rate pool from sessionSets: [{rate, remaining, serviceName}, ...]
  const ratePool = sortedSets.map(s => ({
    rate: Number(s.rate) || 0,
    remaining: Number(s.qty) || 0,
    serviceName: s.serviceName || "",
    date: s.date,
  }));

  // Build timeline items: therapy appointments + dividers (no consultation appts)
  const sortedAppts = [...therapyAppts].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Determine which completed sessions are unpaid (cumulative visitCharges > totalPaidSoFar)
  let runningCharges = 0;
  const unpaidSet = new Set();
  const partialPaidSet = new Set(); // at most 1 — the crossover session
  let ppFound = false;
  const completedByDate = sortedAppts.filter(a => a.status.toLowerCase() === "completed");
  for (const a of completedByDate) {
    const prevRunning = runningCharges;
    runningCharges += Number(a.visitCharges) || 0;
    if (runningCharges > paid) {
      unpaidSet.add(a._id);
      if (!ppFound && prevRunning < paid && paid > 0) {
        partialPaidSet.add(a._id);
        ppFound = true;
      }
    }
  }
  const fullyPaidCount = completedByDate.length - unpaidSet.size;
  const ppCount = partialPaidSet.size;

  // Assign each completed session an expected rate by matching designation to pool
  const poolCopy = ratePool.map(p => ({ ...p }));
  const expectedRateMap = {};
  const highestRatePool = ratePool.length > 0
    ? ratePool.reduce((best, p) => p.rate > best.rate ? p : best, ratePool[0])
    : null;

  const normalize = (s) => s
    .replace(/\bsr\b/gi, "senior")
    .replace(/\bdr\b/gi, "doctor")
    .replace(/\bjr\b/gi, "junior")
    .replace(/\bsn\b/gi, "senior")
    .replace(/\basst\b/gi, "assistant")
    .toLowerCase().trim();

  for (const a of completedByDate) {
    const designation = (a.leadTherapistDesignation || "").toLowerCase().trim();
    const desigNorm = normalize(designation);
    let matched = null;

    if (designation) {
      const candidates = poolCopy
        .filter(p => p.remaining > 0 && p.serviceName)
        .map(p => {
          const svcNorm = normalize(p.serviceName);
          const afterWith = svcNorm.includes(" with ") ? svcNorm.split(" with ").pop().trim() : svcNorm;
          const exactMatch = afterWith === desigNorm;
          const containsMatch = svcNorm.includes(desigNorm);
          return { pool: p, exactMatch, containsMatch, afterWith };
        });

      const exact = candidates.find(c => c.exactMatch);
      if (exact) matched = exact.pool;

      if (!matched) {
        const containing = candidates
          .filter(c => c.containsMatch)
          .sort((a, b) => a.afterWith.length - b.afterWith.length);
        if (containing.length > 0) matched = containing[0].pool;
      }
    }

    if (!matched) matched = poolCopy.find(p => p.remaining > 0);

    if (matched) {
      expectedRateMap[a._id] = { rate: matched.rate, serviceName: matched.serviceName, matchType: "pool" };
      matched.remaining--;
    } else if (highestRatePool) {
      expectedRateMap[a._id] = { rate: highestRatePool.rate, serviceName: highestRatePool.serviceName, matchType: "fallback" };
    }
  }

  // Find the divider position
  let paidExhaustedIdx = -1;
  let lastPaidCompletedIdx = -1;
  runningCharges = 0;
  for (let i = 0; i < sortedAppts.length; i++) {
    const a = sortedAppts[i];
    if (a.status.toLowerCase() === "completed") {
      runningCharges += Number(a.visitCharges) || 0;
      if (runningCharges > paid && paidExhaustedIdx < 0) {
        paidExhaustedIdx = lastPaidCompletedIdx >= 0 ? lastPaidCompletedIdx + 1 : i;
      }
      if (runningCharges <= paid) {
        lastPaidCompletedIdx = i;
      }
    }
  }
  if (paidExhaustedIdx < 0 && paid > 0 && totalQtyBought > 0 && completedByDate.length >= totalQtyBought) {
    paidExhaustedIdx = lastPaidCompletedIdx >= 0 ? lastPaidCompletedIdx + 1 : -1;
  }

  // Money-based pending
  const used = Number(overallVisitCharges) || 0;
  const remainingBudget = paid - used;
  const remainingPool = poolCopy.filter(p => p.remaining > 0);
  const remainingSlots = remainingPool.reduce((s, p) => s + p.remaining, 0);
  const weightedRemRate = remainingSlots > 0
    ? remainingPool.reduce((s, p) => s + p.rate * p.remaining, 0) / remainingSlots
    : 0;
  const moneyPending = (weightedRemRate > 0 && remainingBudget > 0)
    ? Math.floor(remainingBudget / weightedRemRate)
    : (remainingBudget <= 0 ? 0 : remainingSlots);
  const qtyPending = remainingSlots;
  // Only show money-based warning when sessions have actually been completed
  // (before any usage, the rate-based estimate is unreliable)
  const pendingChanged = (completedByDate.length > 0 && remainingSlots > 0 && moneyPending !== qtyPending);

  // Group sessionSets by billId for consultation hover details
  const billGroups = {};
  for (const s of sortedSets) {
    const bid = s.billId || "unknown";
    if (!billGroups[bid]) billGroups[bid] = { date: s.date, services: [] };
    billGroups[bid].services.push({
      name: s.serviceName || "Therapy Sessions",
      qty: Number(s.qty) || 0,
      rate: Number(s.rate) || 0,
    });
  }

  // Build a map of billId → payment status from bills data
  const billPaymentMap = {}; // billId → { total, paid, balance, paymentmode, invoicedate }
  for (const b of billsList) {
    const bid = b._id || b.id;
    if (bid) {
      billPaymentMap[bid] = {
        total: Number(b.total) || 0,
        paid: Number(b.paid) || 0,
        balance: Number(b.balance) || 0,
        paymentmode: b.paymentmode || '',
        invoicedate: b.invoicedate || b.createdAt,
      };
    }
  }

  // Helper: get date string (YYYY-MM-DD) for same-day comparison
  const toDateKey = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // Build billing markers with payment status
  const billingMarkers = []; // { date, dateKey, billId, label, tooltip, payStatus, totalQty }
  const insertedBillIds = new Set();
  for (const s of sortedSets) {
    const bid = s.billId || "unknown";
    if (insertedBillIds.has(bid)) continue;
    insertedBillIds.add(bid);
    const group = billGroups[bid];
    const totalQty = group.services.reduce((sum, sv) => sum + sv.qty, 0);
    const detailLines = group.services.map(sv =>
      `${sv.qty} × ${sv.name}${sv.rate ? " @ ₹" + sv.rate : ""}`
    ).join("\n");
    const dateStr = new Date(group.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

    // Payment status from bills
    const bp = billPaymentMap[bid];
    let payStatus = "unknown"; // unknown, full, partial, unpaid
    let payLine = "";
    if (bp) {
      if (bp.balance <= 0) { payStatus = "full"; payLine = `\nPayment: ₹${bp.paid} PAID (${bp.paymentmode})`; }
      else if (bp.paid > 0) { payStatus = "partial"; payLine = `\nPayment: ₹${bp.paid}/${bp.total} (₹${bp.balance} due)`; }
      else { payStatus = "unpaid"; payLine = `\nPayment: UNPAID (₹${bp.total} due)`; }
    }

    billingMarkers.push({
      date: group.date,
      dateKey: toDateKey(group.date),
      billId: bid,
      label: `₹`,
      totalQty,
      payStatus,
      tooltip: `Billing — ${dateStr}\n${detailLines}\nTotal: ${totalQty} sessions${payLine}`,
    });
  }

  // Build consultation appointment markers — match each to nearest GTP for rich tooltip
  const gtpPlans = consultObj?.gtpPlans || [];
  const consultMarkers = consultAppts.map(a => {
    const aDate = new Date(a.date).getTime();
    const dateStr = new Date(a.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
    let tip = `Consultation — ${dateStr}\n${a.status}${a.leadTherapist ? "\nDr. " + a.leadTherapist : ""}`;
    let gtpNum = null;

    // Find the GTP closest to this consultation appointment date
    if (gtpPlans.length > 0) {
      let bestGtp = null, bestDist = Infinity;
      for (const g of gtpPlans) {
        const gDate = new Date(g.date).getTime();
        const dist = Math.abs(aDate - gDate);
        if (dist < bestDist) { bestDist = dist; bestGtp = g; }
      }
      // Only match if within 48 hours
      if (bestGtp && bestDist < 48 * 60 * 60 * 1000) {
        gtpNum = bestGtp.gtpNumber;
        const svcs = (bestGtp.suggestedServices || []).filter(s => !(s.name || '').toLowerCase().includes('consultation'));
        const svcSessionTotal = svcs.reduce((sum, s) => sum + actualSessionQty(s), 0);
        const sessT = Number(bestGtp.mythsessions) || 0;
        const sessM = Number(bestGtp.maintsessions) || 0;
        const sessP = Number(bestGtp.preventivesessions) || 0;
        const sessTotal = svcSessionTotal > 0 ? svcSessionTotal : (sessT + sessM + sessP);
        const statusLabel = bestGtp.status === 'rejected' ? ' [REJECTED]'
          : bestGtp.status === 'pending_approval' ? ' [PENDING]' : '';

        tip = `GTP ${gtpNum}${statusLabel} — ${dateStr}`;
        tip += `\n${a.status}${a.leadTherapist ? " · Dr. " + a.leadTherapist : ""}`;
        if (sessTotal > 0) tip += `\nTotal sessions: ${sessTotal}`;
        if (svcs.length > 0) {
          tip += `\nServices:`;
          svcs.forEach(s => {
            const aq = actualSessionQty(s);
            const pkgNote = aq !== (Number(s.qty) || 0) ? ` (${s.qty} pkg × ${s._masterQty})` : '';
            tip += `\n  ${s.name} ×${aq}${pkgNote}`;
          });
        }
        if (svcs.length === 0 && sessTotal === 0) tip += `\nExercises only`;
      }
    }

    return { date: a.date, dateKey: toDateKey(a.date), tooltip: tip, status: a.status, gtpNum };
  });

  // Group billing + consultation by dateKey for same-day stacking
  const stackGroups = {}; // dateKey → { bills: [], consults: [] }
  for (const bm of billingMarkers) {
    if (!stackGroups[bm.dateKey]) stackGroups[bm.dateKey] = { bills: [], consults: [], date: bm.date };
    stackGroups[bm.dateKey].bills.push(bm);
  }
  for (const cm of consultMarkers) {
    if (!stackGroups[cm.dateKey]) stackGroups[cm.dateKey] = { bills: [], consults: [], date: cm.date };
    stackGroups[cm.dateKey].consults.push(cm);
  }

  // Sort stack groups by date
  const sortedStackGroups = Object.values(stackGroups).sort((a, b) => new Date(a.date) - new Date(b.date));

  // Build timeline: stacked billing/consultation + session circles + warning dividers
  const timeline = [];
  let stackIdx = 0;

  for (let i = 0; i < sortedAppts.length; i++) {
    const a = sortedAppts[i];
    const apptDate = new Date(a.date || a.createdAt);

    // Insert stacked billing/consultation markers when we cross their date
    while (stackIdx < sortedStackGroups.length && new Date(sortedStackGroups[stackIdx].date) <= apptDate) {
      const sg = sortedStackGroups[stackIdx];
      timeline.push({ type: "stack", group: sg });
      stackIdx++;
    }

    // Insert "paid sessions exhausted" warning divider
    if (paidExhaustedIdx === i && paid > 0) {
      timeline.push({ type: "divider", label: "Paid sessions exhausted", date: a.date, warning: true });
    }

    const isMissed = a.status.toLowerCase() === "skipped" ||
      (a.status.toLowerCase() === "scheduled" && apptDate < today);
    const isCompleted = a.status.toLowerCase() === "completed";
    const isFuture = a.status.toLowerCase() === "scheduled" && apptDate >= today;
    const isUnpaid = isCompleted && unpaidSet.has(a._id);

    timeline.push({ type: "session", appt: a, isMissed, isCompleted, isFuture, isUnpaid, date: a.date });
  }

  // Insert remaining stacked markers after all appointments
  while (stackIdx < sortedStackGroups.length) {
    timeline.push({ type: "stack", group: sortedStackGroups[stackIdx] });
    stackIdx++;
  }

  // Use whichever is larger: bought qty or actual sessions (prevents "5/1" when billing is stale)
  const total = totalQtyBought > 0 ? Math.max(totalQtyBought, therapyAppts.length) : therapyAppts.length;
  const pct = total > 0 ? Math.round((completedSessions / total) * 100) : 0;
  const missedCount = therapyAppts.filter(a =>
    a.status.toLowerCase() === "skipped" ||
    (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today)
  ).length;
  const unpaidCount = unpaidSet.size;
  const due = used - paid;

  // Payment status color for billing boxes
  const billBoxColor = (payStatus) => {
    if (payStatus === "full") return { bg: "#4caf50", border: "#2e7d32" }; // green = fully paid
    if (payStatus === "partial") return { bg: "#ff9800", border: "#e65100" }; // amber = partial
    if (payStatus === "unpaid") return { bg: "#f44336", border: "#c62828" }; // red = unpaid
    return { bg: "#1565c0", border: "#0d47a1" }; // blue = unknown
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "16px 18px",
      border: "1px solid #e8ecf4",
      boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={sectionTitleStyle}>SESSION PROGRESS</span>
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          <span title="Completed sessions" style={{ color: pct >= 70 ? "#2e7d32" : pct >= 40 ? "#e65100" : "#b71c1c" }}>{completedSessions} Done</span>
          <span style={{ color: "#94a3b8" }}> / </span>
          <span title={"Paid: " + fullyPaidCount + " of " + completedSessions + " completed sessions are paid (FIFO)"} style={{ color: "#0d9488" }}>{fullyPaidCount} Paid</span>
          {ppCount > 0 && <>
            <span style={{ color: "#94a3b8" }}> / </span>
            <span title="Partially Paid session (straddles payment boundary)" style={{ color: "#d97706" }}>{ppCount} Partial</span>
          </>}
          <span style={{ color: "#94a3b8" }}> / </span>
          <span title={"Bought: " + totalQtyBought + " sessions purchased via billing"} style={{ color: "#1565c0" }}>{totalQtyBought} Bought</span>
          {(recSess || 0) > 0 && <>
            <span style={{ color: "#94a3b8" }}> / </span>
            <span title={"Recommended: " + recSess + " sessions prescribed in consultation"} style={{ color: "#7b1fa2" }}>{recSess} Rx</span>
          </>}
        </span>
      </div>
      {/* Per-GTP R breakdown badges */}
      {consultObj?.gtpPlans?.length > 1 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
          {consultObj.gtpPlans.map(gtp => {
            const svcQty = (gtp.suggestedServices || []).filter(s => !isConsultationSession(s))
              .reduce((sum, s) => sum + actualSessionQty(s), 0);
            const gs = svcQty > 0 ? svcQty
              : (gtp.mythsessions || 0) + (gtp.maintsessions || 0) + (gtp.preventivesessions || 0);
            const isRejected = gtp.status === 'rejected';
            return (
              <span key={gtp.gtpNumber} style={{
                fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
                background: isRejected ? "#fef2f2" : gs > 0 ? "#f3e8ff" : "#f1f5f9",
                color: isRejected ? "#dc2626" : gs > 0 ? "#7c3aed" : "#94a3b8",
                border: `1px solid ${isRejected ? "#fecaca" : gs > 0 ? "#e9d5ff" : "#e2e8f0"}`,
                textDecoration: isRejected ? "line-through" : "none",
                opacity: isRejected ? 0.6 : 1,
              }}>
                GTP{gtp.gtpNumber}: {gs > 0 ? gs : "ex"}
              </span>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
        {timeline.map((item, i) => {
          // Stacked billing + consultation boxes
          if (item.type === "stack") {
            const { bills: gBills, consults: gConsults } = item.group;
            // Determine stacking order: billing first if it came before consultation, else consultation first
            // Use the actual dates. If same day, billing on top by default (pre-paid scenario)
            const hasBill = gBills.length > 0;
            const hasConsult = gConsults.length > 0;

            return (
              <div key={`stack-${i}`} style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                marginBottom: 6, cursor: "help", gap: 2,
              }}>
                {/* Billing box(es) — color shows payment status (financial roles only) */}
                {gBills.map((bm, bi) => {
                  const c = canSeeFinancials ? billBoxColor(bm.payStatus) : { bg: "#9ca3af", border: "#6b7280" };
                  const tip = canSeeFinancials ? bm.tooltip : bm.tooltip.split("\nPayment:")[0];
                  return (
                    <span key={`bill-${bi}`} title={tip} style={{
                      width: 20, height: 20, borderRadius: 4,
                      background: c.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 8, fontWeight: 800,
                      border: `1.5px solid ${c.border}`,
                      boxShadow: `0 1px 3px ${c.bg}44`,
                    }}>
                      {bm.totalQty}
                    </span>
                  );
                })}
                {/* Consultation box(es) — stacked below billing if same day, show GTP# */}
                {gConsults.map((cm, ci) => (
                  <span key={`cons-${ci}`} title={cm.tooltip} style={{
                    width: 20, height: 20, borderRadius: 4,
                    background: "#e8567f",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: cm.gtpNum ? 7 : 9, fontWeight: 800,
                    border: "1.5px solid #c9415f",
                    boxShadow: "0 1px 3px rgba(232,86,127,0.3)",
                  }}>
                    {cm.gtpNum ? `G${cm.gtpNum}` : "C"}
                  </span>
                ))}
              </div>
            );
          }

          if (item.type === "divider") {
            // Hide financial warning dividers from non-financial roles
            if (item.warning && !canSeeFinancials) return null;
            return (
              <div key={`div-${i}`} title={item.label} style={{
                display: "flex", flexDirection: "column", alignItems: "center", margin: "0 2px",
              }}>
                <div style={{
                  width: 2, height: 24, background: item.warning ? "#d32f2f" : "#9e9e9e",
                  borderRadius: 1,
                }} />
                <span style={{
                  fontSize: 8, color: item.warning ? "#d32f2f" : "#757575",
                  fontWeight: 700, whiteSpace: "nowrap", marginTop: 1,
                }}>
                  {item.warning ? "⚠" : "│"}
                </span>
              </div>
            );
          }

          const { appt: a, isMissed, isCompleted, isFuture, isUnpaid } = item;
          const showUnpaid = canSeeFinancials && isUnpaid;
          const tType = getTherapistType(a);
          const tColor = THERAPIST_COLORS[tType];
          // Fill = therapist-type color always (golden doctor, blue therapist); border = payment status
          const color = isMissed ? "#d32f2f" : tColor || "#bdbdbd";
          const label = isCompleted ? (showUnpaid ? "U" : "P") : isMissed ? "✗" : isFuture ? "·" : "○";
          const labelColor = isCompleted ? "#000" : "#fff";
          // Border: green = paid, red = unpaid, none for future/missed
          const borderColor = isCompleted
            ? (showUnpaid ? "#d32f2f" : "#4caf50")
            : "none";
          const dateStr = new Date(a.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

          const vc = Number(a.visitCharges) || 0;
          const expected = expectedRateMap[a._id];
          const expectedRate = expected ? expected.rate : 0;
          const diff = expectedRate > 0 ? vc - expectedRate : 0;
          const showAboveLine = canSeeFinancials && isCompleted && expectedRate > 0 && vc > 0 && diff > 0;
          const showBelowLine = canSeeFinancials && isCompleted && expectedRate > 0 && vc > 0 && diff < 0;

          const chargeTooltip = canSeeFinancials
            ? ((isCompleted && vc > 0 && expectedRate > 0)
              ? `\nCharged: ₹${vc} | Package rate: ₹${expectedRate}${expected.serviceName ? " (" + expected.serviceName + ")" : ""} | Diff: ${diff > 0 ? "+" : ""}₹${Math.round(diff)}`
              : (vc > 0 ? `\nCharged: ₹${vc}` : ""))
            : "";
          const unpaidStr = showUnpaid ? " [UNPAID]" : "";
          const therapistStr = a.leadTherapist ? `\nLed by: ${a.leadTherapist}${a.leadTherapistDesignation ? " (" + a.leadTherapistDesignation + ")" : ""}` : "";

          return (
            <div key={i} title={`${dateStr} — ${a.status}${therapistStr}${chargeTooltip}${unpaidStr}${a.sessionComments ? "\n" + a.sessionComments.slice(0, 80) : ""}`}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "default" }}>
              <div style={{
                width: 16, height: showAboveLine ? 3 : 0,
                background: "#4caf50", borderRadius: 2, marginBottom: showAboveLine ? 1 : 0,
              }} />
              <span style={{
                width: 24, height: 24, borderRadius: "50%", background: color,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: labelColor, fontSize: 10, fontWeight: 800, flexShrink: 0,
                border: borderColor !== "none" ? `2px solid ${borderColor}` : "none",
              }}>
                {label}
              </span>
              <div style={{
                width: 16, height: showBelowLine ? 3 : 0,
                background: "#ff9800", borderRadius: 2, marginTop: showBelowLine ? 1 : 0,
              }} />
            </div>
          );
        })}
      </div>
      <div style={{ height: 10, borderRadius: 5, background: "#e0e0e0", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: pct >= 70 ? "#4caf50" : pct >= 40 ? "#ff9800" : "#f44336",
          borderRadius: 5, transition: "width 0.6s ease",
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", marginTop: 4 }}>
        <span>{pct}% completion rate</span>
        <span>
          {missedCount > 0 && <span style={{ color: "#d32f2f" }}>{missedCount} missed · </span>}
          {canSeeFinancials && unpaidCount > 0 && <span style={{ color: "#d32f2f" }}>{unpaidCount} unpaid · </span>}
          <span style={{ color: "#4caf50" }}>{completedSessions} completed</span>
        </span>
      </div>
      {/* CPR type table moved to parent-level side-by-side layout */}
      {/* Legend for billing box colors — financial roles only */}
      {canSeeFinancials && billingMarkers.length > 0 && (
        <div style={{ display: "flex", gap: 10, fontSize: 10, color: "#888", marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#b8860b", display: "inline-block" }} /> Doctor
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#1565c0", display: "inline-block" }} /> Therapist
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #4caf50", display: "inline-block", boxSizing: "border-box" }} /> Paid (P)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #d32f2f", display: "inline-block", boxSizing: "border-box" }} /> Unpaid (U)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "#d32f2f", display: "inline-block" }} /> Missed
          </span>
        </div>
      )}
      {totalQtyBought > 0 && (
        <div style={{ fontSize: 11, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
          <span
            title={canSeeFinancials && pendingChanged
              ? `Package: ${qtyPending} of ${totalQtyBought} sessions remaining | Based on usage: ${moneyPending} sessions available at current rates (₹${remainingBudget > 0 ? remainingBudget : 0} remaining ÷ ~₹${Math.round(weightedRemRate)}/session)`
              : `${qtyPending} of ${totalQtyBought} sessions remaining`}
            style={{
              color: canSeeFinancials && pendingChanged
                ? (moneyPending < qtyPending ? "#d32f2f" : "#2e7d32")
                : "#555",
              fontWeight: canSeeFinancials && pendingChanged ? 700 : 400,
              cursor: "help",
            }}
          >
            {canSeeFinancials && pendingChanged
              ? `⚠ ${moneyPending} sessions affordable (${qtyPending} of ${totalQtyBought} remaining) — ${moneyPending < qtyPending ? "higher-rate sessions used" : "lower-rate sessions used"}`
              : `${qtyPending} of ${totalQtyBought} sessions remaining`}
          </span>
        </div>
      )}
      {canSeeFinancials && paid > 0 && (
        <div style={{ fontSize: 11, color: due > 0 ? "#d32f2f" : due < 0 ? "#2e7d32" : "#888", marginTop: 4 }}>
          ₹{paid} paid · ₹{used} charged{due > 0 ? ` · ₹${due} balance remaining` : due < 0 ? ` · ₹${Math.abs(due)} advance balance` : ""}
        </div>
      )}
    </div>
  );
}

/* ===============================
   COMM ITEM
================================ */
function CommItem({ item }) {
  const dateStr = item.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

  if (item.kind === "session") {
    const isCompleted = item.status === "completed";
    const esc = item.escalationInfo;
    return (
      <div style={{
        padding: "10px 14px", borderRadius: 10, fontSize: 12,
        background: isCompleted ? "#f0fdf4" : "#fef2f2",
        borderLeft: `3px solid ${isCompleted ? "#22c55e" : "#ef4444"}`,
        fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
      }}>
        {/* Row 1: status, date, therapist, charges */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontWeight: 700 }}>
            {isCompleted ? "✅" : "❌"} Session — {dateStr}
          </span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {item.by && <span style={{ color: "#666", fontSize: 11 }}>{item.by}{item.designation ? ` (${item.designation})` : ''}</span>}
          </div>
        </div>

        {/* Session comments */}
        {item.text && <div style={{ color: "#444", marginBottom: 3 }}>💬 {item.text.slice(0, 200)}{item.text.length > 200 ? "…" : ""}</div>}

        {/* Doctor/Patient comments */}
        {item.commentsByDr && <div style={{ color: "#1565c0", fontSize: 11, marginBottom: 2 }}>👨‍⚕️ Dr: {item.commentsByDr}</div>}
        {item.commentsByPt && <div style={{ color: "#7b1fa2", fontSize: 11, marginBottom: 2 }}>🧑 Patient: {item.commentsByPt}</div>}

        {/* Escalation badge + details */}
        {item.escalated && (
          <div style={{ background: "#fff3cd", borderRadius: 6, padding: "5px 8px", marginTop: 4, border: "1px solid #fcd34d" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>⬆ ESCALATED</span>
              {esc?.issueCategory && <span style={{ fontSize: 10, background: "#fee2e2", color: "#991b1b", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>{esc.issueCategory}</span>}
              {esc?.escalatedToName && <span style={{ fontSize: 11, color: "#1d4ed8" }}>→ {esc.escalatedToName}</span>}
              {esc?.escalatedTo && !esc?.escalatedToName && <span style={{ fontSize: 11, color: "#1d4ed8" }}>→ {esc.escalatedTo}</span>}
            </div>
            {esc?.commentsByThe && <div style={{ fontSize: 11, color: "#78350f", marginTop: 3 }}>Reason: {esc.commentsByThe}</div>}
            {esc?.commentsByDr && <div style={{ fontSize: 11, color: "#1565c0", marginTop: 2 }}>Dr response: {esc.commentsByDr}</div>}
            {esc?.commentsByAdmin && <div style={{ fontSize: 11, color: "#4a5568", marginTop: 2 }}>Admin: {esc.commentsByAdmin}</div>}
            {esc?.commentsByIncharge && <div style={{ fontSize: 11, color: "#4a5568", marginTop: 2 }}>Incharge: {esc.commentsByIncharge}</div>}
            {esc?.escalationAckDate && <div style={{ fontSize: 10, color: "#16a34a", marginTop: 2 }}>✓ Acknowledged {new Date(esc.escalationAckDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>}
          </div>
        )}

        {/* Missed session: follow-up attempts by MYC team */}
        {!isCompleted && item.followUpAttempts && item.followUpAttempts.length > 0 && (
          <div style={{ background: "#eff6ff", borderRadius: 6, padding: "5px 8px", marginTop: 4, border: "1px solid #bfdbfe" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1e40af", marginBottom: 3 }}>📞 Follow-up attempts after missed session:</div>
            {item.followUpAttempts.map((f, i) => (
              <div key={i} style={{ fontSize: 11, color: "#374151", marginBottom: 2 }}>
                {f.mode === "call" ? "📞" : f.mode === "whatsapp" ? "💬" : "📝"}{' '}
                {new Date(f.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                {f.summary ? ` — ${f.summary.slice(0, 100)}` : ' — no notes'}
              </div>
            ))}
          </div>
        )}
        {!isCompleted && (!item.followUpAttempts || item.followUpAttempts.length === 0) && (
          <div style={{ fontSize: 11, color: "#dc2626", fontStyle: "italic", marginTop: 3 }}>
            ⚠ No follow-up recorded after missed session
          </div>
        )}

        {/* No session notes warning */}
        {!item.text && isCompleted && <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>No session notes recorded</span>}
      </div>
    );
  }

  if (item.kind === "whatsapp") {
    return (
      <div style={{
        padding: "10px 14px", borderRadius: 10, fontSize: 12,
        background: "#f0fdf9", borderLeft: "3px solid #22c55e",
        fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: item.text ? 3 : 0 }}>
          <span style={{ fontWeight: 700 }}>💬 WhatsApp — {dateStr}</span>
          {item.msgType && <span style={{ color: "#999", fontSize: 11 }}>{item.msgType}</span>}
        </div>
        {item.text && <span style={{ color: "#444" }}>{item.text.slice(0, 140)}{item.text.length > 140 ? "…" : ""}</span>}
      </div>
    );
  }

  if (item.kind === "calllog") {
    return (
      <div style={{
        padding: "10px 14px", borderRadius: 10, fontSize: 12,
        background: "#eff6ff", borderLeft: "3px solid #3b82f6",
        fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontWeight: 700 }}>
            {item.mode === "whatsapp" ? "💬 WA Log" : "📞 Call Log"} — {dateStr}
          </span>
          {item.escalateTo && (
            <span style={{ fontSize: 10, background: "#fff3cd", color: "#856404", padding: "1px 6px", borderRadius: 10, fontWeight: 700 }}>
              ⬆ {item.escalateTo}
            </span>
          )}
        </div>
        {item.topics && item.topics.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 4 }}>
            {item.topics.map(t => (
              <span key={t} style={{ fontSize: 10, background: "#dbeafe", color: "#1e40af", padding: "1px 6px", borderRadius: 10 }}>{t}</span>
            ))}
          </div>
        )}
        {item.summary && <div style={{ color: "#333", marginBottom: 3 }}>{item.summary}</div>}
        {item.comments && <div style={{ color: "#555", fontStyle: "italic" }}>{item.comments}</div>}
        {item.nextAppt && <div style={{ color: "#2e7d32", fontSize: 11, marginTop: 2 }}>📅 Next appt: {item.nextAppt}</div>}
        {item.nextFollowup && <div style={{ color: "#e65100", fontSize: 11 }}>⏰ Follow-up: {item.nextFollowup}</div>}
        {item.stopFollowup && <div style={{ color: "#b71c1c", fontSize: 11 }}>🛑 Follow-up stopped</div>}
      </div>
    );
  }

  if (item.kind === "followup") {
    return (
      <div style={{
        padding: "10px 14px", borderRadius: 10, fontSize: 12,
        background: "#fefce8", borderLeft: "3px solid #eab308",
        fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
      }}>
        <div style={{ fontWeight: 700, marginBottom: item.text ? 3 : 0 }}>📋 Follow-up Note — {dateStr}</div>
        {item.text && <span style={{ color: "#444" }}>{item.text.slice(0, 140)}{item.text.length > 140 ? "…" : ""}</span>}
      </div>
    );
  }

  if (item.kind === "kitschedule") {
    const isSent = item.status === "sent";
    const isSkipped = item.status === "skipped";
    const isFuture = item.status === "pending" && item.date > new Date();
    const isDue = item.status === "pending" && item.date <= new Date();
    const borderColor = isSent ? "#22c55e" : isSkipped ? "#d1d5db" : isFuture ? "#818cf8" : "#f59e0b";
    const bg = isSent ? "#f0fdf4" : isSkipped ? "#f9f9f9" : isFuture ? "#eef2ff" : "#fffbeb";
    return (
      <div style={{ padding: "10px 14px", borderRadius: 10, fontSize: 12, background: bg, borderLeft: `3px solid ${borderColor}`, opacity: isSkipped ? 0.6 : 1, fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
          <span style={{ fontWeight: 700 }}>
            {item.icon} KIT Day {item.day} — {item.title}
          </span>
          <span style={{
            fontSize: 10, padding: "1px 6px", borderRadius: 8, fontWeight: 700,
            background: isSent ? "#dcfce7" : isSkipped ? "#e5e7eb" : isFuture ? "#e0e7ff" : "#fef08a",
            color: isSent ? "#15803d" : isSkipped ? "#6b7280" : isFuture ? "#4338ca" : "#854d0e",
          }}>
            {isSent ? "✓ Sent" : isSkipped ? "Skipped" : isFuture ? "📅 Scheduled" : "⏰ Due Now"}
          </span>
        </div>
        {item.campaignName && <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>🚀 {item.campaignName}</div>}
        <div style={{ color: isSkipped ? "#9ca3af" : "#555", fontSize: 11, lineHeight: 1.4 }}>
          {(item.text || "").slice(0, 120)}{(item.text || "").length > 120 ? "…" : ""}
        </div>
        <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
          {isSent
            ? `Sent ${new Date(item.sentDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
            : `Scheduled ${dateStr}`}
        </div>
      </div>
    );
  }

  return null;
}

/* ===============================
   MAIN EXPANDED ROW
================================ */
export default function SaadhakExpandedRow({ data }) {
  const { auth } = useAuth();
  const canSeeFinancials = canDo(auth, 'billing.view');
  const isOwnerSolution = auth?.roles?.some(r => ['owner', 'solution'].includes(r));

  const [appointments, setAppointments] = useState(null);
  const [calllead, setCalllead] = useState(undefined);
  const [patientProfile, setPatientProfile] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logModal, setLogModal] = useState(null); // { mode: 'call'|'whatsapp' }
  const [assignedVideos, setAssignedVideos] = useState([]);
  const [assignedPlaylists, setAssignedPlaylists] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [consultation, setConsultation] = useState(null);
  const [watchProgressMap, setWatchProgressMap] = useState({});

  const patientCard = data.patientCard;
  const phone = data.number;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(process.env.REACT_APP_URL + `/appointments/type/Therapy/patient/${patientCard}`)
        .then(r => r.data || []).catch(() => []),
      phone
        ? api.get(process.env.REACT_APP_URL + `/callleads/profiles/number/${phone}`)
            .then(r => r.data || null).catch(() => null)
        : Promise.resolve(null),
      api.get(process.env.REACT_APP_URL + `/patients/profiles/number/${data.number}`)
        .then(r => r.data || null).catch(() => null),
      api.get(process.env.REACT_APP_URL + `/billings/${patientCard}`)
        .then(r => r.data || []).catch(() => []),
      api.get(process.env.REACT_APP_URL + `/youtube/videos/patient/${patientCard}`)
        .then(r => r.data || []).catch(() => []),
      api.get(process.env.REACT_APP_URL + `/youtube/playlists/patient/${patientCard}`)
        .then(r => r.data || []).catch(() => []),
      api.get(process.env.REACT_APP_URL + `/patients/consultation/${patientCard}`)
        .then(r => r.data || null).catch(() => null),
      api.get(process.env.REACT_APP_URL + `/resource-videos/watch-progress/${patientCard}`)
        .then(r => r.data || []).catch(() => []),
    ]).then(([appts, lead, patientProfile, billsData, vids, pls, consultData, wpData]) => {
      if (!cancelled) {
        setAppointments(appts);
        setCalllead(lead);
        setPatientProfile(patientProfile);
        setBills(billsData);
        setAssignedVideos(vids);
        setAssignedPlaylists(pls);
        setConsultation(consultData);
        var wpMap = {};
        (Array.isArray(wpData) ? wpData : []).forEach(function(wp) { if (wp.videoId) wpMap[wp.videoId] = wp; });
        setWatchProgressMap(wpMap);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [patientCard, phone, data.number]);

  if (loading) {
    return <div style={{ padding: "24px 20px", color: "#94a3b8", fontSize: 13, fontStyle: "italic", fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif" }}>Loading patient details…</div>;
  }

  const today = new Date();
  const risk = calculatePatientRisk(appointments);
  const situation = getRiskSituation(risk, appointments);
  const sortedAppts = [...appointments].sort((a, b) => new Date(a.date) - new Date(b.date));
  const completedSessions = sortedAppts.filter(a => a.status.toLowerCase() === "completed" && !isConsultationAppt(a)).length;
  const lastAttended = risk.stoppedDays >= 60 ? null : risk.stoppedDays;
  const allSessionSets = (patientProfile?.sessionSets || data.sessionSets || []).filter(s => !isConsultationSession(s));
  const totalQtyBought = allSessionSets.reduce((sum, s) => sum + (Number(s.qty) || 0), 0);
  // R = total qty from suggestedServices (excludes consultation-type services for consistency with P)
  // Falls back to session count fields if suggestedServices is empty (legacy)
  const recommendedSessions = (() => {
    if (!consultation) return 0;
    const ss = (consultation.suggestedServices || []).filter(s => !isConsultationSession(s));
    if (ss.length > 0) {
      // Use actualSessionQty to expand packages (qty × _masterQty)
      return ss.reduce((sum, s) => sum + actualSessionQty(s), 0);
    }
    return (Number(consultation.mythsessions) || 0) + (Number(consultation.maintsessions) || 0) +
      (Number(consultation.preventivesessions) || 0) + (Number(consultation.othersessions1) || 0) +
      (Number(consultation.othersessions2) || 0) + (Number(consultation.othersessions3) || 0);
  })();

  // ── Total session duration across all completed sessions ──
  const totalSessionDurationMin = sortedAppts
    .filter(a => a.status.toLowerCase() === "completed" && !isConsultationAppt(a))
    .reduce((sum, a) => {
      if (a.actualStartTime && a.actualEndTime) {
        return sum + (new Date(a.actualEndTime) - new Date(a.actualStartTime)) / 60000;
      }
      if (a.startTime && a.actualEndTime) {
        return sum + (new Date(a.actualEndTime) - new Date(a.startTime)) / 60000;
      }
      if (a.start && a.end) {
        return sum + (new Date(a.end) - new Date(a.start)) / 60000;
      }
      if (a.timeSpent) return sum + (Number(a.timeSpent) || 0);
      return sum;
    }, 0);
  const totalDurH = Math.floor(totalSessionDurationMin / 60);
  const totalDurM = Math.round(totalSessionDurationMin % 60);

  // ── Expected session time from exercise plan (same formula as SIV) ──
  const exerciseSeqs = (consultation?.sequences || []);
  const expectedSessionSec = exerciseSeqs.reduce((sum, s) => {
    const dur = Number(s.duration) || 0;
    const secs = dur <= 0 ? 60 : (s.durationUnit === 'minutes' ? dur * 60 : dur);
    return sum + secs * (s.repetition || 1);
  }, 0);
  const expectedSessionMin = exerciseSeqs.length > 0 ? Math.max(10, Math.ceil(expectedSessionSec / 60)) : 0;

  // ── Per-type C/P/R breakdown (rendered in side panel outside SessionTimeline) ──
  const therapyAppts = sortedAppts.filter(a => !isConsultationAppt(a));
  const completedByDateOuter = therapyAppts.filter(a => a.status.toLowerCase() === "completed")
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const outerTypeCounts = { doctor: 0, senior: 0, therapist: 0 };
  for (const a of completedByDateOuter) outerTypeCounts[getTherapistType(a)]++;
  const outerTypeBought = { doctor: 0, senior: 0, therapist: 0 };
  for (const s of allSessionSets) {
    const sn = (s.serviceName || "").toLowerCase();
    if (sn.includes("doctor") || sn.includes("dr")) outerTypeBought.doctor += Number(s.qty) || 0;
    else if (sn.includes("senior") || sn.includes("sr")) outerTypeBought.senior += Number(s.qty) || 0;
    else outerTypeBought.therapist += Number(s.qty) || 0;
  }
  // ── FIFO-paid sessions per type ──
  // P  = fully paid (cumulative charges ≤ totalPaidSoFar)
  // PP = partially paid (the ONE crossover session where payment runs out mid-session)
  const paidSoFar = Number(data.totalPaidSoFar) || 0;
  const outerTypePaid = { doctor: 0, senior: 0, therapist: 0 };
  const outerTypePP   = { doctor: 0, senior: 0, therapist: 0 };
  let totalPaidSessions = 0;
  let totalPPSessions   = 0;
  {
    let running = 0;
    let ppFound = false;
    for (const a of completedByDateOuter) {
      const prevRunning = running;
      running += Number(a.visitCharges) || 0;
      const tType = getTherapistType(a);
      if (running <= paidSoFar) {
        // Fully paid — cumulative still within paid amount
        outerTypePaid[tType]++;
        totalPaidSessions++;
      } else if (!ppFound && prevRunning < paidSoFar) {
        // Partially paid — payment ran out during THIS session
        outerTypePP[tType]++;
        totalPPSessions++;
        ppFound = true;
      }
      // else: completely unpaid (prevRunning >= paidSoFar) — skip
    }
  }
  // U: unpaid sessions per type
  const outerTypeUnpaid = {
    doctor:    outerTypeCounts.doctor    - outerTypePaid.doctor    - outerTypePP.doctor,
    senior:    outerTypeCounts.senior    - outerTypePaid.senior    - outerTypePP.senior,
    therapist: outerTypeCounts.therapist - outerTypePaid.therapist - outerTypePP.therapist,
  };
  const totalUnpaidSessions = completedSessions - totalPaidSessions - totalPPSessions;

  const outerTypeRecommended = (() => {
    const tr = { doctor: 0, senior: 0, therapist: 0 };
    if (!consultation) return tr;
    const ss = (consultation.suggestedServices || []).filter(s => !isConsultationSession(s));
    if (ss.length > 0) {
      for (const s of ss) {
        const sn = (s.name || '').toLowerCase();
        const q = actualSessionQty(s);
        if (sn.includes('doctor') || sn.includes('dr')) tr.doctor += q;
        else if (sn.includes('senior') || sn.includes('sr')) tr.senior += q;
        else tr.therapist += q;
      }
      return tr;
    }
    tr.doctor = Number(consultation.mythsessions) || 0;
    tr.senior = Number(consultation.maintsessions) || 0;
    tr.therapist = Number(consultation.preventivesessions) || 0;
    return tr;
  })();
  const hasOuterTypeCounts = totalQtyBought > 0 || recommendedSessions > 0;

  /* Escalation lookup from patient profile */
  const escalations = patientProfile?.previousEscalations || [];

  /* Unified comm timeline */
  const sessionComms = sortedAppts
    .filter(a => {
      const isMissed = a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
      return isMissed || a.status.toLowerCase() === "completed";
    })
    .map(a => {
      const isMissed = a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
      const apptDate = new Date(a.date);

      // Find matching escalation (within ±2 days of this session)
      const matchedEsc = a.escalated ? escalations.find(e => {
        if (!e.escalationDate) return false;
        const diff = Math.abs(new Date(e.escalationDate) - apptDate);
        return diff < 2 * 86400000; // ±2 days
      }) : null;

      // For missed sessions, find follow-up attempts (call/WA logs within 3 days after)
      let followUpAttempts = [];
      if (isMissed) {
        const fups = calllead?.followUpHistory || [];
        followUpAttempts = fups.filter(f => {
          const fDate = new Date(f.date || f.updatedAt);
          const diff = fDate - apptDate;
          return diff >= 0 && diff < 3 * 86400000;
        }).map(f => ({
          mode: f.mode || 'note',
          summary: f.summary || f.comments || f.enquiryDetails || '',
          date: new Date(f.date || f.updatedAt),
        }));
      }

      return {
        date: apptDate,
        kind: "session",
        status: isMissed ? "missed" : "completed",
        text: a.sessionComments || "",
        by: a.leadTherapist || a.username || "",
        designation: a.leadTherapistDesignation || "",
        visitCharges: a.visitCharges || 0,
        escalated: a.escalated || false,
        escalationInfo: matchedEsc || null,
        commentsByDr: a.commentsByDr || "",
        commentsByPt: a.commentsByPt || "",
        commentsByThe: a.commentsByThe || "",
        followUpAttempts,
      };
    });

  const waComms = (calllead?.messageHistory || []).map(m => ({
    date: new Date(m.date), kind: "whatsapp", text: m.text || "", msgType: m.type || "",
  }));

  const fupComms = (calllead?.followUpHistory || []).map(f => {
    if (f.mode) {
      return {
        date: new Date(f.date || f.updatedAt),
        kind: "calllog",
        mode: f.mode,
        summary: f.summary || "",
        topics: f.topics || [],
        nextAppt: f.nextAppt || "",
        nextFollowup: f.nextFollowup || "",
        stopFollowup: f.stopFollowup || false,
        escalateTo: f.escalateTo || "",
        comments: f.enquiryDetails || f.comments || "",
      };
    }
    return { date: new Date(f.date || f.updatedAt), kind: "followup", text: f.comments || f.enquiryDetails || "" };
  });

  const kitComms = (calllead?.kitSchedule || []).map(m => ({
    date: new Date(m.status === "sent" && m.sentDate ? m.sentDate : m.scheduledDate),
    kind: "kitschedule",
    status: m.status,
    icon: m.icon || "🚀",
    title: m.title,
    text: m.status === "sent" ? (m.sentText || m.text) : m.text,
    day: m.day,
    sentDate: m.sentDate,
    campaignName: m.campaignName,
  }));

  // Past comms sorted newest-first, then upcoming KIT messages at the top
  const pastComms = [...sessionComms, ...waComms, ...fupComms]
    .sort((a, b) => b.date - a.date)
    .slice(0, 30);
  const upcomingKit = kitComms.filter(k => k.status === "pending" && k.date > today).sort((a, b) => a.date - b.date);
  const pastKit = kitComms.filter(k => k.status !== "pending" || k.date <= today);
  const allComms = [...upcomingKit, ...pastKit, ...pastComms].slice(0, 40);

  const handleLogSaved = (updates) => {
    setCalllead(prev => prev ? { ...prev, ...updates } : prev);
  };

  return (
    <div style={{
      borderLeft: `5px solid ${risk.color}`,
      background: 'linear-gradient(135deg, #fafbff 0%, #f0f4fa 100%)',
      padding: '20px 24px 24px',
      fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
    }}>

      {/* Call Log Modal */}
      {logModal && (
        <CallLogModal
          patient={data}
          calllead={calllead}
          mode={logModal.mode}
          phone={phone}
          onClose={() => setLogModal(null)}
          onSaved={handleLogSaved}
        />
      )}

      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16,
        background: '#fff', borderRadius: 14, padding: '14px 18px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.03)',
        border: '1px solid #e8ecf4',
      }}>
        <strong style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: -0.3 }}>{data.name}</strong>
        <span style={{
          padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
          background: situation.bg, color: situation.color,
          border: `1px solid ${situation.color}22`,
        }}>
          {situation.label}
        </span>
        <span style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>📱 {phone || "—"}</span>
        {phone && (
          <>
            <button
              onClick={e => { e.stopPropagation(); setLogModal({ mode: "call" }); }}
              style={actionBtnStyle("#1d4ed8")}
            >
              📞 Call
            </button>
            <button
              onClick={e => { e.stopPropagation(); setLogModal({ mode: "whatsapp" }); }}
              style={actionBtnStyle("#059669")}
            >
              💬 WhatsApp
            </button>
          </>
        )}
        {lastAttended !== null ? (
          <span style={{
            padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: lastAttended > 21 ? "#fef2f2" : lastAttended > 10 ? "#fffbeb" : "#ecfdf5",
            color: lastAttended > 21 ? "#b91c1c" : lastAttended > 10 ? "#d97706" : "#059669",
            border: `1px solid ${lastAttended > 21 ? "#fca5a522" : lastAttended > 10 ? "#fbbf2422" : "#34d39922"}`,
          }}>
            🕐 Last attended {lastAttended === 0 ? "today" : `${lastAttended}d ago`}
          </span>
        ) : (
          <span style={{
            padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: "#faf5ff", color: "#7c3aed", border: '1px solid #c4b5fd33',
          }}>
            ⚠️ Never attended
          </span>
        )}
        {calllead?.kitActive && (
          <span style={{
            padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: "#ecfdf5", color: "#047857", border: '1px solid #6ee7b733',
          }}>
            🚀 KIT Active
          </span>
        )}
        {calllead?.followUpDate && (
          <span style={{
            padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: new Date(calllead.followUpDate) < today ? "#fef2f2" : "#fffbeb",
            color: new Date(calllead.followUpDate) < today ? "#b91c1c" : "#d97706",
            border: `1px solid ${new Date(calllead.followUpDate) < today ? "#fca5a522" : "#fbbf2422"}`,
          }}>
            {new Date(calllead.followUpDate) < today ? "⚠️ Follow-up overdue" : "📅 Follow-up"}: {new Date(calllead.followUpDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
          </span>
        )}
      </div>

      {/* Financial + Status Strip */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center',
      }}>
        {data.therapist && (
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
            👨‍⚕️ <strong style={{ color: '#1e293b' }}>{data.therapist}</strong>
          </span>
        )}
        {canSeeFinancials && (() => {
          const totalBilled = Number(data.totalSoFar) || 0;
          const totalPaid = Number(data.totalPaidSoFar) || 0;
          const pending = totalBilled - totalPaid;
          if (totalBilled === 0 && totalPaid === 0) return null;
          return (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', padding: '3px 10px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span>Total <strong style={{ color: '#1e293b' }}>₹{totalBilled.toLocaleString('en-IN')}</strong></span>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <span>Paid <strong style={{ color: '#059669' }}>₹{totalPaid.toLocaleString('en-IN')}</strong></span>
              {pending !== 0 && <>
                <span style={{ color: '#cbd5e1' }}>|</span>
                <span style={{ color: pending > 0 ? '#dc2626' : '#059669', fontWeight: 700 }}>
                  {pending > 0 ? `Pending ₹${pending.toLocaleString('en-IN')}` : `Credit ₹${Math.abs(pending).toLocaleString('en-IN')}`}
                </span>
              </>}
            </span>
          );
        })()}
      </div>

      {/* Video Assignments (owner/solution only) */}
      {isOwnerSolution && (assignedVideos.length > 0 || assignedPlaylists.length > 0) && (
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setShowVideoModal(true)}
            style={{
              background: 'linear-gradient(135deg,#7c3aed11,#6366f111)', border: '1px solid #7c3aed33',
              borderRadius: 10, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            }}
          >
            <span style={{ fontSize: 16 }}>🎬</span>
            <span style={{ flex: 1, textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>
              {assignedVideos.length} video{assignedVideos.length !== 1 ? 's' : ''} assigned
              {assignedPlaylists.length > 0 && ` · ${assignedPlaylists.length} playlist${assignedPlaylists.length !== 1 ? 's' : ''}`}
              {(() => {
                var done = assignedVideos.filter(function(v) { var wp = watchProgressMap[v.youtubeId]; return wp && wp.completed; }).length;
                return done > 0 ? ` · ${done} completed` : '';
              })()}
            </span>
            <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>View ›</span>
          </button>
        </div>
      )}
      {showVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowVideoModal(false)}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 520, width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>🎬 Assigned Videos — {data.patientCard}</span>
              <button onClick={() => setShowVideoModal(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '12px 20px' }}>
              {/* Overall progress summary */}
              {(() => {
                var total = assignedVideos.length;
                var completed = assignedVideos.filter(function(v) { var wp = watchProgressMap[v.youtubeId]; return wp && wp.completed; }).length;
                var inProg = assignedVideos.filter(function(v) { var wp = watchProgressMap[v.youtubeId]; return wp && !wp.completed && wp.percentWatched > 0; }).length;
                var notStarted = total - completed - inProg;
                return total > 0 ? (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                    <span style={{ background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 }}>{completed}/{total} Completed</span>
                    <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 }}>{inProg} In Progress</span>
                    <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 }}>{notStarted} Not Started</span>
                  </div>
                ) : null;
              })()}
              {assignedPlaylists.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Playlists</div>
                  {assignedPlaylists.map(pl => {
                    var plVids = assignedVideos.filter(function(v) { return v.playlistId === pl.playlistId; });
                    var plDone = plVids.filter(function(v) { var wp = watchProgressMap[v.youtubeId]; return wp && wp.completed; }).length;
                    var plTotal = plVids.length || (pl.videoCount || 0);
                    return (
                    <div key={pl._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: '1px solid #e2e8f0' }}>
                      {pl.thumbnail && <img src={pl.thumbnail} alt="" style={{ width: 48, height: 36, borderRadius: 6, objectFit: 'cover' }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.title || pl.playlistId}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{plTotal} videos{pl.sequential ? ' · Sequential' : ''} · {plDone}/{plTotal} completed</div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              {assignedVideos.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Videos</div>
                  {assignedVideos.map(v => {
                    var wp = watchProgressMap[v.youtubeId] || {};
                    var pct = wp.percentWatched || 0;
                    var done = !!wp.completed;
                    var barColor = done ? '#22c55e' : pct > 0 ? '#3b82f6' : '#e2e8f0';
                    var label = done ? 'Completed' : pct > 0 ? pct + '% watched' : 'Not started';
                    var labelColor = done ? '#166534' : pct > 0 ? '#1e40af' : '#94a3b8';
                    return (
                    <div key={v._id} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {v.thumbnail && <img src={v.thumbnail} alt="" style={{ width: 48, height: 36, borderRadius: 6, objectFit: 'cover' }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title || v.youtubeId}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{v.videoType === 'short' ? 'Short' : 'Video'}{v.duration ? ` · ${Math.floor(v.duration / 60)}:${String(v.duration % 60).padStart(2, '0')}` : ''}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: labelColor, whiteSpace: 'nowrap' }}>{label}</span>
                      </div>
                      <div style={{ marginTop: 5, background: '#e2e8f0', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                        <div style={{ width: (done ? 100 : pct) + '%', height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                      {done && wp.completedAt && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>Completed {new Date(wp.completedAt).toLocaleDateString('en-IN')}</div>}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
              <button
                onClick={() => { setShowVideoModal(false); window.location.href = '/Resources'; }}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >Assign or Edit More</button>
            </div>
          </div>
        </div>
      )}

      {/* Session Timeline with refresh */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLoading(true);
              Promise.all([
                api.get(process.env.REACT_APP_URL + `/appointments/type/Therapy/patient/${patientCard}`)
                  .then(r => r.data || []).catch(() => []),
                api.get(process.env.REACT_APP_URL + `/patients/profiles/number/${data.number}`)
                  .then(r => r.data || null).catch(() => null),
                api.get(process.env.REACT_APP_URL + `/billings/${patientCard}`)
                  .then(r => r.data || []).catch(() => []),
              ]).then(([appts, prof, billsData]) => {
                setAppointments(appts);
                setPatientProfile(prof);
                setBills(billsData);
                setLoading(false);
              });
            }}
            style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, color: "#64748b", cursor: "pointer", padding: "4px 10px", fontWeight: 600, fontFamily: "'Inter',sans-serif", transition: 'all 0.15s' }}
          >↻ Refresh</button>
        </div>
        <SessionTimeline
            appointments={sortedAppts}
            completedSessions={completedSessions}
            today={today}
            totalPaidSoFar={data.totalPaidSoFar || 0}
            overallVisitCharges={data.overallVisitCharges || 0}
            sessionSets={patientProfile?.sessionSets || data.sessionSets || []}
            bills={bills}
            canSeeFinancials={canSeeFinancials}
            recommendedSessions={recommendedSessions}
            consultation={consultation}
          />
      </div>

      {/* Escalation History Summary */}
      {escalations.length > 0 && (
        <div style={{ background: "#fffbf5", border: "1px solid #fed7aa44", borderRadius: 14, padding: "14px 18px", marginTop: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div style={sectionTitleStyle}>⬆ ESCALATION HISTORY ({escalations.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {escalations.slice(0, 5).map((esc, i) => (
              <div key={i} style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: "#92400e", fontWeight: 600 }}>
                  {esc.escalationDate ? new Date(esc.escalationDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : '—'}
                </span>
                {esc.issueCategory && <span style={{ fontSize: 10, background: "#fee2e2", color: "#991b1b", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>{esc.issueCategory}</span>}
                <span style={{ color: "#1d4ed8", fontSize: 11 }}>→ {esc.escalatedToName || esc.escalatedTo || '—'}</span>
                {esc.commentsByThe && <span style={{ color: "#555", fontSize: 11 }}>"{esc.commentsByThe.slice(0, 80)}{esc.commentsByThe.length > 80 ? '…' : ''}"</span>}
                {esc.escalationAckDate
                  ? <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>✓ Ack'd</span>
                  : <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>⏳ Pending</span>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Attendance This Week / Month / Total ── */}
      {(() => {
        var now = new Date();
        var startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay()); startOfWeek.setHours(0,0,0,0);
        var startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        var completed = therapyAppts.filter(function(a) { return a.status.toLowerCase() === 'completed'; });
        var thisWeek = completed.filter(function(a) { return new Date(a.date) >= startOfWeek; }).length;
        var thisMonth = completed.filter(function(a) { return new Date(a.date) >= startOfMonth; }).length;
        var missed = therapyAppts.filter(function(a) {
          var s = a.status.toLowerCase();
          return s === 'skipped' || (s === 'scheduled' && new Date(a.date) < now);
        });
        var missedWeek = missed.filter(function(a) { return new Date(a.date) >= startOfWeek; }).length;
        var missedMonth = missed.filter(function(a) { return new Date(a.date) >= startOfMonth; }).length;
        var daysPerWeek = Number(data.daysperweek) || 0;
        return (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 0', minWidth: 80, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#16a34a' }}>{thisWeek}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>This Week</div>
              {missedWeek > 0 && <div style={{ fontSize: 9, color: '#dc2626', fontWeight: 700, marginTop: 2 }}>{missedWeek} missed</div>}
              {daysPerWeek > 0 && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>of {daysPerWeek}/wk plan</div>}
            </div>
            <div style={{ flex: '1 1 0', minWidth: 80, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>{thisMonth}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>This Month</div>
              {missedMonth > 0 && <div style={{ fontSize: 9, color: '#dc2626', fontWeight: 700, marginTop: 2 }}>{missedMonth} missed</div>}
            </div>
            <div style={{ flex: '1 1 0', minWidth: 80, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#7c3aed' }}>{completedSessions}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Total Done</div>
              {recommendedSessions > 0 && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>of {recommendedSessions} planned</div>}
            </div>
            <div style={{ flex: '1 1 0', minWidth: 80, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#d97706' }}>{totalDurH > 0 ? totalDurH + 'h ' : ''}{totalDurM}m</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Total Duration</div>
              {completedSessions > 0 && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>avg {Math.round(totalSessionDurationMin / completedSessions)}m/session</div>}
            </div>
            {expectedSessionMin > 0 && (
            <div style={{ flex: '1 1 0', minWidth: 80, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>{expectedSessionMin}m</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Expected / Session</div>
              <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>{exerciseSeqs.length} exercises</div>
            </div>
            )}
          </div>
        );
      })()}

      {/* ── Latest Session Feedback (pain trend) ── */}
      {(() => {
        var withFeedback = therapyAppts
          .filter(function(a) { return a.status.toLowerCase() === 'completed' && a.sessionFeedback && a.sessionFeedback.pain != null; })
          .sort(function(a, b) { return new Date(b.date) - new Date(a.date); })
          .slice(0, 5);
        if (withFeedback.length === 0) return null;
        var latest = withFeedback[0].sessionFeedback;
        var symptoms = [
          { key: 'pain', label: 'Pain', color: '#dc2626' },
          { key: 'numbness', label: 'Numb', color: '#d97706' },
          { key: 'stiffness', label: 'Stiff', color: '#ea580c' },
          { key: 'weakness', label: 'Weak', color: '#9333ea' },
        ].filter(function(s) { return latest[s.key] != null; });
        if (symptoms.length === 0) return null;
        // Check trend: compare latest vs oldest in the 5
        var oldest = withFeedback[withFeedback.length - 1].sessionFeedback;
        return (
          <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 14, padding: '14px 18px', marginTop: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <div style={sectionTitleStyle}>📊 LATEST SESSION FEEDBACK</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {symptoms.map(function(s) {
                var val = latest[s.key];
                var oldVal = oldest[s.key];
                var trend = oldVal != null && withFeedback.length > 1 ? (val < oldVal ? '↓' : val > oldVal ? '↑' : '→') : '';
                var trendColor = trend === '↓' ? '#16a34a' : trend === '↑' ? '#dc2626' : '#94a3b8';
                return (
                  <div key={s.key} style={{ textAlign: 'center', minWidth: 50 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{val}<span style={{ fontSize: 10 }}>/10</span></div>
                    <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
                    {trend && <div style={{ fontSize: 12, color: trendColor, fontWeight: 800 }}>{trend}</div>}
                  </div>
                );
              })}
              {latest.overallImprovement != null && (
                <div style={{ textAlign: 'center', minWidth: 50 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#16a34a' }}>{latest.overallImprovement}<span style={{ fontSize: 10 }}>/10</span></div>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Improve</div>
                </div>
              )}
              {latest.notes && (
                <div style={{ flex: '1 1 100%', fontSize: 11, color: '#475569', fontStyle: 'italic', marginTop: 4 }}>
                  "{latest.notes.slice(0, 120)}{latest.notes.length > 120 ? '…' : ''}"
                </div>
              )}
            </div>
            {withFeedback.length > 1 && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>
                Trend based on last {withFeedback.length} sessions ({new Date(withFeedback[withFeedback.length - 1].date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} → {new Date(withFeedback[0].date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Call Records from Appointments ── */}
      {(() => {
        var allCallRecords = [];
        therapyAppts.forEach(function(a) {
          if (a.callrecords && a.callrecords.length > 0) {
            a.callrecords.forEach(function(cr) {
              allCallRecords.push({
                date: new Date(cr.date),
                mode: cr.mode || 'call',
                status: cr.status || '',
                summary: cr.summary || '',
                template: cr.template || '',
                notes: cr.notes || '',
                loggedBy: cr.loggedBy || '',
                apptDate: a.date,
                patientName: a.name || (a.title || '').split('/')[0].trim(),
              });
            });
          }
        });
        allCallRecords.sort(function(a, b) { return b.date - a.date; });
        if (allCallRecords.length === 0) return null;
        return (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 14, padding: '14px 18px', marginTop: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <div style={sectionTitleStyle}>📞 CALL RECORDS ({allCallRecords.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
              {allCallRecords.slice(0, 15).map(function(cr, i) {
                var statusColors = {
                  successful: { bg: '#dcfce7', color: '#16a34a' },
                  no_answer: { bg: '#fef3c7', color: '#d97706' },
                  busy: { bg: '#fff7ed', color: '#ea580c' },
                  unreachable: { bg: '#fef2f2', color: '#dc2626' },
                  wrong_number: { bg: '#f5f3ff', color: '#7c3aed' },
                };
                var sc = statusColors[cr.status] || { bg: '#f1f5f9', color: '#64748b' };
                return (
                  <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <span style={{ color: '#0369a1', fontWeight: 600, flexShrink: 0 }}>
                      {cr.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                    <span style={{ fontSize: 14 }}>{cr.mode === 'whatsapp' ? '\uD83D\uDCAC' : '\uD83D\uDCDE'}</span>
                    <span style={{ fontSize: 10, background: sc.bg, color: sc.color, padding: '1px 8px', borderRadius: 8, fontWeight: 700, flexShrink: 0 }}>
                      {(cr.status || '').replace(/_/g, ' ')}
                    </span>
                    {cr.summary && <span style={{ color: '#334155', fontSize: 11 }}>{cr.summary.slice(0, 100)}{cr.summary.length > 100 ? '…' : ''}</span>}
                    {cr.loggedBy && <span style={{ color: '#94a3b8', fontSize: 10 }}>by {cr.loggedBy}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* CPR Table + Recent Communications — side by side */}
      <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "flex-start" }}>
        {/* CPR Type Breakdown — 50% */}
        {hasOuterTypeCounts && (
          <div style={{
            flex: '1 1 50%', minWidth: 0, background: '#fff', borderRadius: 14, padding: '14px 16px',
            border: '1px solid #e8ecf4', boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          }}>
            <div style={sectionTitleStyle}>C / P / PP / U / B / R</div>
            <div style={{ border: "1px solid #e8ecf4", borderRadius: 10, overflow: "hidden", fontSize: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 26px 26px 26px 26px 26px 26px", background: "#f8fafc", borderBottom: "1px solid #e8ecf4", padding: "5px 6px", fontWeight: 700, color: "#64748b", fontSize: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
                <span>Type</span>
                <span style={{ textAlign: "center", color: "#2e7d32" }} title="Completed">C</span>
                <span style={{ textAlign: "center", color: "#0d9488" }} title="Fully Paid (FIFO)">P</span>
                <span style={{ textAlign: "center", color: "#d97706" }} title="Partially Paid">PP</span>
                <span style={{ textAlign: "center", color: "#d32f2f" }} title="Unpaid">U</span>
                <span style={{ textAlign: "center", color: "#1565c0" }} title="Bought/Billed">B</span>
                <span style={{ textAlign: "center", color: "#7b1fa2" }} title="Recommended">R</span>
              </div>
              {[
                { key: "therapist", label: "Therapist", color: "#1976d2" },
                { key: "senior",    label: "Sr. Therapist", color: "#1565c0" },
                { key: "doctor",    label: "Doctor", color: "#b8860b" },
              ].map(row => (
                <div key={row.key} style={{ display: "grid", gridTemplateColumns: "1fr 26px 26px 26px 26px 26px 26px", padding: "4px 6px", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: row.color, display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: "#334155", fontSize: 10 }}>{row.label}</span>
                  </span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypeCounts[row.key] > 0 ? "#2e7d32" : "#cbd5e1" }}>{outerTypeCounts[row.key]}</span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypePaid[row.key] > 0 ? "#0d9488" : "#cbd5e1" }}>{outerTypePaid[row.key]}</span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypePP[row.key] > 0 ? "#d97706" : "#cbd5e1" }}>{outerTypePP[row.key]}</span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypeUnpaid[row.key] > 0 ? "#d32f2f" : "#cbd5e1" }}>{outerTypeUnpaid[row.key]}</span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypeBought[row.key] > 0 ? "#1565c0" : "#cbd5e1" }}>{outerTypeBought[row.key]}</span>
                  <span style={{ textAlign: "center", fontWeight: 700, color: outerTypeRecommended[row.key] > 0 ? "#7b1fa2" : "#cbd5e1" }}>{outerTypeRecommended[row.key]}</span>
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 26px 26px 26px 26px 26px 26px", padding: "5px 6px", background: "#f8fafc", fontWeight: 800, fontSize: 11 }}>
                <span style={{ color: "#0f172a" }}>Total</span>
                <span style={{ textAlign: "center", color: "#2e7d32" }}>{completedSessions}</span>
                <span style={{ textAlign: "center", color: "#0d9488" }}>{totalPaidSessions}</span>
                <span style={{ textAlign: "center", color: "#d97706" }}>{totalPPSessions}</span>
                <span style={{ textAlign: "center", color: "#d32f2f" }}>{totalUnpaidSessions}</span>
                <span style={{ textAlign: "center", color: "#1565c0" }}>{totalQtyBought}</span>
                <span style={{ textAlign: "center", color: "#7b1fa2" }}>{recommendedSessions || 0}</span>
              </div>
            </div>
          </div>
        )}
        {/* Communication Timeline — 50% */}
        <div style={{
          flex: '1 1 50%', minWidth: 0, background: '#fff', borderRadius: 14, padding: '14px 16px',
          border: '1px solid #e8ecf4', boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}>
          <div style={sectionTitleStyle}>💬 RECENT COMMUNICATIONS</div>
          {allComms.length === 0 ? (
            <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No communication history found.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
              {allComms.map((item, i) => <CommItem key={i} item={item} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Shared micro-styles ── */
const sectionTitleStyle = {
  fontSize: 10, fontWeight: 800, color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, display: "block",
  fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
};

const actionBtnStyle = (bg) => ({
  padding: "5px 14px", background: bg, color: "#fff",
  borderRadius: 8, border: "none", fontSize: 12, fontWeight: 700,
  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
  boxShadow: `0 2px 6px ${bg}33`,
  transition: 'all 0.15s',
  fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
});
